import { useMemo } from 'react';
import { scaleSequential } from 'd3-scale';
import { interpolateYlGnBu, interpolatePlasma, interpolateCool, interpolatePuBuGn,
    interpolateSinebow, interpolateCividis, interpolateViridis, interpolateBlues,
interpolateBuPu } from 'd3-scale-chromatic';


/**
 * Custom hook to generate color scales based on a sequential color scheme.
 *
 * @param {object} config - Configuration object for colors.
 * @param {function} [config.interpolateFunction=interpolateYlGnBu] - The D3 interpolation function.
 * @param {number} [config.numberOfShades=5] - Number of color shades to generate.
 * @param {number} [config.minDomain=0] - Minimum value for the domain.
 * @param {number} [config.maxDomain=1] - Maximum value for the domain.
 * @param {string} [config.format='rgb'] - Color format, either 'rgb' (default) or 'hex'.
 * @returns {string[]} Array of color values.
 */
const useColors = ({
  interpolateFunction = interpolateBlues,
  numberOfShades = 5,
  minDomain = 0,
  maxDomain = 1,
  format = 'rgb', // New parameter to determine color format
}) => {
  const interpolateColors = interpolateFunction || interpolateBlues;
  const validNumberOfShades = Math.max(1, Math.round(numberOfShades));
  const colorScale = useMemo(() => {
    return scaleSequential(interpolateColors).domain([minDomain, maxDomain]);
  }, [interpolateFunction, minDomain, maxDomain]);

  const colors = useMemo(() => {
    return Array.from({ length: validNumberOfShades }, (_, i) => {
      const rgbColor = colorScale(
        i / (validNumberOfShades - 1) * (maxDomain - minDomain) + minDomain
      );

      // Convert to hex if format is 'hex', otherwise return rgb
      if (format === 'hex') {
        const [r, g, b] = rgbColor
          .match(/\d+/g)
          .map((val) => parseInt(val, 10)); // Extract RGB components
        return `#${((1 << 24) + (r << 16) + (g << 8) + b)
          .toString(16)
          .slice(1)}`;
      }

      return rgbColor; // Default 'rgb'
    }).reverse();
  }, [colorScale, validNumberOfShades, minDomain, maxDomain, format]);

  return colors;
};

export default useColors;
